import { Box } from '@chakra-ui/react';
import {
  type ColumnDef,
  type ColumnSizingInfoState,
  type ColumnSizingState,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type PaginationState,
  type RowData,
  type RowSelectionState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ReplotChartNames } from '../../constants';
import { plotTestId } from '../../utilities/plotTestId';
import { useGetBodyRows } from './body-rows';
import { getColumns, resolveTextAlignFn } from './columns';
import { useGetHeaderRows } from './header-rows';
import { RenderTableContent } from './render-table';
import styles from './table-chart.module.css';
import { TablePagination } from './table-pagination';

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    isNumeric?: boolean;
    align?: 'left' | 'center' | 'right';
  }
}

export type TableChartProps<TData extends object = object> = {
  data: TData[];
  columns?: ColumnDef<TData, unknown>[];
  searchTerm?: string;
  showZebraStripe?: boolean;
  isPaginated?: boolean;
  enableRowSelection?: boolean;
  onRowSelectionChange?: (selectedRows: TData[]) => void;
  rowSelection?: RowSelectionState;
  onRowSelectionStateChange?: OnChangeFn<RowSelectionState>;
  onSortedDataChange?: (sortedRows: TData[]) => void;
  boundaryRef?: React.RefObject<HTMLDivElement>;
  onRenderRow?: (params: {
    rowContent: ReactNode;
    rowData: TData;
    rowIndex: number;
    gridRef: HTMLDivElement | null;
  }) => ReactNode;
  onSelectRow?: (data: object) => void;
  className?: string;
  serverSidePagination?: {
    externalPaginationState: PaginationState;
    onPaginationChange: OnChangeFn<PaginationState>;
    totalItems: number;
    pageSizeOptions?: number[];
    hidePageSizeSelector?: boolean;
  };
  serverSideSorting?: {
    externalSortingState: SortingState;
    onSortingChange: OnChangeFn<SortingState>;
    onHeaderClick?: (columnId: string) => void;
  };
  controlledSorting?: {
    externalSortingState: SortingState;
    onSortingChange: OnChangeFn<SortingState>;
  };
  onSortingStateNotify?: (sorting: SortingState) => void;
  autoFitColumns?: boolean;
};

export const TableChart = <TData extends object = object>({
  data,
  columns,
  searchTerm = '',
  showZebraStripe = true,
  isPaginated = false,
  enableRowSelection = false,
  onRowSelectionChange,
  rowSelection: controlledRowSelection,
  onRowSelectionStateChange,
  onSortedDataChange,
  boundaryRef,
  onRenderRow,
  onSelectRow,
  className,
  serverSidePagination,
  serverSideSorting,
  controlledSorting,
  onSortingStateNotify,
  autoFitColumns = false,
}: TableChartProps<TData>) => {
  const autoColumns = getColumns({ columns, enableRowSelection, data });

  const filteredData = useMemo<TData[]>(() => {
    if (!searchTerm) return data;
    const lower = searchTerm.toLowerCase();
    return data.filter((row) =>
      Object.values(row as object).some((value) =>
        String(value ?? '')
          .toLowerCase()
          .includes(lower)
      )
    );
  }, [data, searchTerm]);

  const resolveTextAlign = resolveTextAlignFn({ columns: autoColumns });

  // Local pagination state used only when pagination is enabled (client-side)
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });

  const isServerSidePagination = !!serverSidePagination;
  const activePaginationState = isServerSidePagination
    ? serverSidePagination.externalPaginationState
    : pagination;
  const activePaginationSetter = isServerSidePagination
    ? serverSidePagination.onPaginationChange
    : setPagination;

  const [sorting, setSorting] = useState<SortingState>([]);

  const isServerSideSorting = !!serverSideSorting;
  const isControlledSorting = !isServerSideSorting && !!controlledSorting;

  const internalSortingSetterWithNotify = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      setSorting((old) =>
        typeof updater === 'function' ? updater(old) : updater
      );
    },
    []
  );

  let activeSortingState = sorting;
  let activeSortingSetter: OnChangeFn<SortingState> = setSorting;

  if (isControlledSorting && controlledSorting) {
    activeSortingState = controlledSorting.externalSortingState;
    activeSortingSetter = controlledSorting.onSortingChange;
  } else if (isServerSideSorting && serverSideSorting) {
    activeSortingState = serverSideSorting.externalSortingState;
    activeSortingSetter = serverSideSorting.onSortingChange;
  } else if (onSortingStateNotify) {
    activeSortingSetter = internalSortingSetterWithNotify;
  }

  useEffect(() => {
    if (!onSortingStateNotify || isControlledSorting || isServerSideSorting) {
      return;
    }
    onSortingStateNotify(sorting);
  }, [sorting, onSortingStateNotify, isControlledSorting, isServerSideSorting]);

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [columnSizingInfo, setColumnSizingInfo] =
    useState<ColumnSizingInfoState>({} as ColumnSizingInfoState);

  const [internalRowSelection, setInternalRowSelection] =
    useState<RowSelectionState>({});
  const activeRowSelection = controlledRowSelection ?? internalRowSelection;
  const activeRowSelectionSetter =
    onRowSelectionStateChange ?? setInternalRowSelection;

  const table = useReactTable({
    data: filteredData,
    columns: autoColumns,
    state: {
      sorting: activeSortingState,
      columnSizing,
      columnSizingInfo,
      ...(isPaginated ? { pagination: activePaginationState } : {}),
      ...(enableRowSelection ? { rowSelection: activeRowSelection } : {}),
    },
    onSortingChange: activeSortingSetter,
    onColumnSizingChange: setColumnSizing,
    onColumnSizingInfoChange: setColumnSizingInfo,
    columnResizeMode: 'onChange',
    enableSorting: true,
    enableMultiSort: false,
    ...(isPaginated
      ? {
          onPaginationChange: activePaginationSetter,
          // For server-side pagination, enable manualPagination to prevent internal state management
          ...(isServerSidePagination
            ? { manualPagination: true }
            : { getPaginationRowModel: getPaginationRowModel() }),
        }
      : {}),
    ...(enableRowSelection
      ? {
          enableRowSelection: true,
          onRowSelectionChange: activeRowSelectionSetter,
          getRowId: (_row: TData, index: number) => String(index),
        }
      : {}),
    ...(isServerSideSorting
      ? { manualSorting: true }
      : { getSortedRowModel: getSortedRowModel() }),
    getCoreRowModel: getCoreRowModel(),
  });

  const isEmpty = filteredData.length === 0;

  const tableSelectedRows = table.getSelectedRowModel().rows;
  // biome-ignore lint/correctness/useExhaustiveDependencies: dependency comparison on objects triggers more than once per selection
  useEffect(() => {
    const selectedRows = tableSelectedRows.map((r) => r.original);
    onRowSelectionChange?.(selectedRows);
  }, [tableSelectedRows.map((r) => r.id).join('|')]);

  const rows = table.getRowModel().rows;
  // With getRowId: String(index), row.id is display order not stable identity; compare original refs to detect reorders.
  const prevSortedOriginalsRef = useRef<TData[]>([]);

  useEffect(() => {
    if (!onSortedDataChange) return;
    const nextSortedRows = rows.map((row) => row.original);
    const isSameOrder =
      prevSortedOriginalsRef.current.length === nextSortedRows.length &&
      prevSortedOriginalsRef.current.every(
        (row, index) => row === nextSortedRows[index]
      );

    if (isSameOrder) return;

    prevSortedOriginalsRef.current = nextSortedRows;
    onSortedDataChange(nextSortedRows);
  }, [rows, onSortedDataChange]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 38,
    overscan: 20,
  });

  const headerRows = useGetHeaderRows({
    table,
    columnSizingInfo,
    onHeaderClick: serverSideSorting?.onHeaderClick,
    autoFitColumns,
    resolveTextAlign,
  });

  const bodyRows = useGetBodyRows({
    items: virtualizer.getVirtualItems(),
    rows,
    enableRowSelection,
    showZebraStripe,
    resolveTextAlign,
    onSelectRow,
    onRenderRow,
    autoFitColumns,
    scrollRef,
  });

  return (
    <Box
      display="flex"
      flexDirection="column"
      w="100%"
      h="100%"
      position="relative"
      data-testid={plotTestId(ReplotChartNames.TableChart)}
      ref={boundaryRef}
      className={className}
    >
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        overflow="hidden"
        className={classNames(styles.containerBorder)}
      >
        <RenderTableContent
          isEmpty={isEmpty}
          useSplitHeaderLayout={!autoFitColumns}
          scrollRef={scrollRef}
          totalHeight={virtualizer.getTotalSize()}
          headerRows={headerRows}
          bodyRows={bodyRows}
        />
      </Box>

      {isPaginated && (
        <TablePagination
          pagination={activePaginationState}
          setPagination={activePaginationSetter}
          totalItems={
            isServerSidePagination
              ? serverSidePagination.totalItems
              : filteredData.length
          }
          pageSizeOptions={serverSidePagination?.pageSizeOptions}
          hidePageSizeSelector={
            serverSidePagination?.hidePageSizeSelector ?? false
          }
        />
      )}
    </Box>
  );
};
